// src/pages/Appli/AppliAdmin/ListingClients/ListingAssociations.jsx

import React, { useState } from "react";

import { FaExclamationTriangle, FaInbox, FaPlus, FaSync } from "react-icons/fa";

import { ToastContainer, toast } from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScrollToTop";

import useAssociations from "./hooks/useAssociations";
import useAssociationFilters from "./hooks/useAssociationFilters";

import AssociationsHeader from "./components/AssociationsHeader";
import AssociationsKpiGrid from "./components/AssociationsKpiGrid";
import { AssociationsFilters } from "./components/AssociationsFilters";
import AssociationsTable from "./components/AssociationsTable";
import AssociationsCards from "./components/AssociationsCards";
import AssociationDrawer from "./components/AssociationDrawer";
import AssociationQuickViewModal from "./components/AssociationQuickViewModal";

import AssociationsAnalytics from "./charts/AssociationsAnalytics";

import { getAssociationApiError } from "./utils/association.utils";

const EMPTY_DRAWER_STATE = {
  isOpen: false,
  association: null,
};

function ListSkeleton() {
  return (
    <div className="space-y-3 p-4">
      {Array.from({ length: 6 }).map((_, index) => (
        <div
          key={index}
          className="h-20 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-800"
        />
      ))}
    </div>
  );
}

function LoadingError({ error, onRetry }) {
  return (
    <div className="flex flex-col items-center justify-center px-4 py-14 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-100 text-xl text-red-600 dark:bg-red-500/15 dark:text-red-300">
        <FaExclamationTriangle />
      </div>

      <h3 className="mt-4 font-semibold text-slate-900 dark:text-white">
        Impossible de charger les associations
      </h3>

      <p className="mt-1 max-w-lg text-sm text-slate-500 dark:text-slate-400">
        {getAssociationApiError(
          error,
          "Le serveur n’a pas pu retourner les associations.",
        )}
      </p>

      <button
        type="button"
        onClick={onRetry}
        className="mt-5 inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-700"
      >
        <FaSync />
        Réessayer
      </button>
    </div>
  );
}

function EmptyList({ hasActiveFilters, onReset, onAdd }) {
  return (
    <div className="flex flex-col items-center justify-center px-4 py-14 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-xl text-slate-500 dark:bg-slate-800 dark:text-slate-300">
        <FaInbox />
      </div>

      <h3 className="mt-4 font-semibold text-slate-900 dark:text-white">
        {hasActiveFilters
          ? "Aucun dossier ne correspond aux filtres"
          : "Aucune association enregistrée"}
      </h3>

      <p className="mt-1 max-w-lg text-sm text-slate-500 dark:text-slate-400">
        {hasActiveFilters
          ? "Modifiez les critères ou réinitialisez les filtres pour retrouver davantage de dossiers."
          : "Ajoutez votre première association ou votre premier prospect."}
      </p>

      <div className="mt-5 flex flex-wrap justify-center gap-2">
        {hasActiveFilters ? (
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Réinitialiser les filtres
          </button>
        ) : null}

        <button
          type="button"
          onClick={onAdd}
          className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-700"
        >
          <FaPlus />
          Ajouter une association
        </button>
      </div>
    </div>
  );
}

export default function ListingAssociations() {
  useScrollToTop();

  const [drawerState, setDrawerState] = useState(EMPTY_DRAWER_STATE);

  const [quickView, setQuickView] = useState(null);

  const [showAnalytics, setShowAnalytics] = useState(false);

  const {
    associations,
    loading,
    loadError,
    saving,
    deletingId,
    updatingStatusIds,

    loadAssociations,
    createAssociation,
    editAssociation,
    removeAssociation,
    changeAssociationStatus,
  } = useAssociations();

  const {
    filters,
    setFilter,
    resetFilters,

    viewMode,
    setViewMode,

    filteredAssociations,
    disciplineOptions,
    productOptions,

    kpis,
    activeFilterCount,
    hasActiveFilters,
  } = useAssociationFilters(associations);

  const openCreateDrawer = () => {
    setDrawerState({
      isOpen: true,
      association: null,
    });
  };

  const openEditDrawer = (associationId, providedAssociation) => {
    const association =
      providedAssociation ||
      associations.find((item) => item.id === associationId);

    if (!association) {
      toast.error("Cette association est introuvable.");
      return;
    }

    setDrawerState({
      isOpen: true,
      association,
    });
  };

  const closeDrawer = () => {
    setDrawerState(EMPTY_DRAWER_STATE);
  };

  const handleDrawerSubmit = async ({ associationId, payload, isEditing }) => {
    try {
      if (isEditing) {
        if (associationId === null || associationId === undefined) {
          throw new Error("Identifiant de l’association manquant.");
        }

        const associationStillExists = associations.some(
          (association) => association.id === associationId,
        );

        if (!associationStillExists) {
          throw new Error("Cette association n’existe plus dans la liste.");
        }

        const updated = await editAssociation(associationId, payload);

        setQuickView((current) => {
          if (current?.id !== associationId) {
            return current;
          }

          return {
            ...current,
            ...updated,
            prospects: updated?.prospects ?? current.prospects ?? [],
          };
        });

        toast.success("Association mise à jour avec succès.");
      } else {
        await createAssociation(payload);

        toast.success("Association ajoutée avec succès.");
      }

      closeDrawer();
    } catch (error) {
      toast.error(
        getAssociationApiError(
          error,
          isEditing ? "La modification a échoué." : "La création a échoué.",
        ),
      );
    }
  };

  const handleDelete = async (associationId) => {
    const association = associations.find((item) => item.id === associationId);

    const associationName = association?.nom_asso || "cette association";

    const confirmed = window.confirm(
      `Supprimer définitivement « ${associationName} » ?`,
    );

    if (!confirmed) return;

    try {
      await removeAssociation(associationId);

      if (quickView?.id === associationId) {
        setQuickView(null);
      }

      if (drawerState.association?.id === associationId) {
        closeDrawer();
      }

      toast.success("Association supprimée.");
    } catch (error) {
      toast.error(getAssociationApiError(error, "La suppression a échoué."));
    }
  };

  const handleStatusChange = async (association, newStatus) => {
    try {
      const updated = await changeAssociationStatus(association, newStatus);

      setQuickView((current) => {
        if (current?.id !== association.id) {
          return current;
        }

        return {
          ...current,
          ...updated,
          statut_asso: newStatus,
          prospects: updated?.prospects ?? current.prospects ?? [],
        };
      });

      toast.success("Statut mis à jour.");
    } catch (error) {
      toast.error(
        getAssociationApiError(error, "La mise à jour du statut a échoué."),
      );
    }
  };

  const listProps = {
    data: filteredAssociations,
    deletingId,
    updatingStatusIds,
    onEdit: openEditDrawer,
    onDelete: handleDelete,
    onQuickView: setQuickView,
    onStatusChange: handleStatusChange,
  };

  const currentQuickView = quickView
    ? associations.find((association) => association.id === quickView.id) ||
      quickView
    : null;

  return (
    <div className="space-y-5 pb-8">
      <AssociationsHeader
        totalCount={associations.length}
        loading={loading}
        showAnalytics={showAnalytics}
        onAdd={openCreateDrawer}
        onRefresh={loadAssociations}
        onToggleAnalytics={() => setShowAnalytics((current) => !current)}
      />

      <AssociationsKpiGrid kpis={kpis} loading={loading} />

      <AssociationsFilters
        filters={filters}
        onFilterChange={setFilter}
        onReset={resetFilters}
        products={productOptions}
        disciplines={disciplineOptions}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        totalCount={filteredAssociations.length}
        activeFilterCount={activeFilterCount}
        hasActiveFilters={hasActiveFilters}
      />

      {loadError && associations.length > 0 ? (
        <div className="flex items-center justify-between gap-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          <span>
            La dernière actualisation a échoué. Les données précédentes restent
            affichées.
          </span>

          <button
            type="button"
            onClick={loadAssociations}
            className="shrink-0 font-semibold underline"
          >
            Réessayer
          </button>
        </div>
      ) : null}

      {showAnalytics ? (
        <AssociationsAnalytics data={filteredAssociations} />
      ) : null}

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        {loading ? (
          <ListSkeleton />
        ) : loadError && associations.length === 0 ? (
          <LoadingError error={loadError} onRetry={loadAssociations} />
        ) : filteredAssociations.length === 0 ? (
          <EmptyList
            hasActiveFilters={hasActiveFilters}
            onReset={resetFilters}
            onAdd={openCreateDrawer}
          />
        ) : (
          <>
            {viewMode === "auto" ? (
              <>
                <div className="hidden md:block">
                  <AssociationsTable {...listProps} />
                </div>

                <div className="md:hidden">
                  <AssociationsCards {...listProps} />
                </div>
              </>
            ) : null}

            {viewMode === "table" ? <AssociationsTable {...listProps} /> : null}

            {viewMode === "cards" ? <AssociationsCards {...listProps} /> : null}
          </>
        )}
      </section>

      {drawerState.isOpen ? (
        <AssociationDrawer
          key={
            drawerState.association?.id
              ? `association-edit-${drawerState.association.id}`
              : "association-create"
          }
          isOpen
          association={drawerState.association}
          saving={saving}
          onClose={closeDrawer}
          onSubmit={handleDrawerSubmit}
        />
      ) : null}

      {currentQuickView ? (
        <AssociationQuickViewModal
          association={currentQuickView}
          onClose={() => setQuickView(null)}
        />
      ) : null}

      <ToastContainer
        position="top-right"
        autoClose={3500}
        newestOnTop
        closeOnClick
        pauseOnHover
      />
    </div>
  );
}
